import { Stack, ToggleButton, ToggleButtonGroup, ToggleButtonGroupProps, Typography } from '@mui/material'
import { Policy } from 'pbts/c1/api/policy/v1/policy'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'

const defaultGetKey = <V extends NonNullable<unknown>>(value: V): string => String(value)

type C1ToggleButtonColor = 'primary' | 'success' | 'error'

type C1ToggleButton<V extends NonNullable<unknown>> = {
    icon?: React.ReactElement
    children?: React.ReactElement | string
    value: V
    disabled?: boolean
    color?: C1ToggleButtonColor
}

// Styling for different button colors when selected
const getColorStyles = (color: C1ToggleButtonColor | undefined): Record<string, unknown> => {
    if (!color || color === 'primary') return {}

    return {
        '&.Mui-selected': {
            backgroundColor: `${color}.main`,
            borderColor: `${color}.main`,
            color: `${color}.contrastText`,
            '&:hover': {
                backgroundColor: `${color}.dark`,
            },
        },
    }
}

type C1ToggleButtonsProps<T extends FieldValues, V extends NonNullable<unknown>> = Omit<ToggleButtonGroupProps, 'onChange' | 'value' | 'children' | 'color'> & {
    control: Control<T>
    name: Path<T>
    policy?: Policy
    options: C1ToggleButton<V>[]
    getKey?: (value: V) => string
    onChange?: (value: V | null) => void
    allowDeselect?: boolean
}

export const C1ToggleButtons = <T extends FieldValues, V extends NonNullable<unknown>>({
    control,
    name,
    options,
    onChange: _onChange,
    getKey = defaultGetKey,
    disabled,
    allowDeselect,
    ...toggleButtonGroupProps }: C1ToggleButtonsProps<T, V>,
): React.ReactElement => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }): React.ReactElement => (
                <ToggleButtonGroup
                    {...toggleButtonGroupProps}
                    value={field.value}
                    disabled={disabled}
                    exclusive
                    onChange={(_, newValue): void => {
                        if (newValue !== null || allowDeselect) {
                            _onChange?.(newValue)
                            field.onChange(newValue)
                        }
                    }}
                >
                    {options.map(({ icon, children, value, disabled: optionDisabled, color }) => (
                        <ToggleButton
                            value={value}
                            key={getKey(value)}
                            aria-pressed={field.value === value}
                            disabled={disabled || optionDisabled}
                            sx={getColorStyles(color)}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                {icon}
                                {children && <Typography component="span">{children}</Typography>}
                            </Stack>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            )}
        />
    )
}
